import React, { useState, useCallback, useEffect } from "react";
import { FaDollarSign, FaMapMarkerAlt, FaCalendarAlt, FaClock, FaTrophy } from "react-icons/fa";
import { useParams, useNavigate, Link } from "react-router-dom";
import { SiCashapp } from "react-icons/si";
import { IoChevronBackOutline, IoReceipt } from "react-icons/io5";
import { HiPencilAlt } from "react-icons/hi";
import { BsPencil } from "react-icons/bs";
import { CiShare2 } from "react-icons/ci";
import { MdDelete } from "react-icons/md";
import { ThreeDots } from 'react-loader-spinner';
import axiosInstance from "../api/axiosInstance";
import API_ENDPOINTS from "../api/endpoints";
import { toast } from 'react-toastify';
import SessionModal from "../components/Modal/SessionModal";
import Select from "react-select";
import { FaEllipsisV, FaEdit, FaTrash } from 'react-icons/fa';
import ConfirmationModal from '../components/Modal/ConfirmationModal';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from 'dayjs';


const customStyles = {
    control: (provided) => ({
        ...provided,
        backgroundColor: "black",
        borderColor: "#292524",
        color: "white",
        minHeight: "48px",
        fontSize: "1rem",
    }),
    singleValue: (provided) => ({
        ...provided,
        color: "white",
    }),
    menu: (provided) => ({
        ...provided,
        backgroundColor: "#111",
        zIndex: 9999,
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? "#1f2937" : "#111",
        color: "white",
    }),
    placeholder: (provided) => ({
        ...provided,
        color: "white",
    }),
    input: (provided) => ({
        ...provided,
        color: "white",
    }),
};


const SessionCard = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [data, setData] = useState({});
    const [subData, setSubData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [sideGameModalOpen, setSideGameModalOpen] = useState(false);
    const [sideGameEditMode, setSideGameEditMode] = useState("add");
    const [showModal, setShowModal] = useState(false);
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [shareContent, setShareContent] = useState("");
    const [shareLoading, setShareLoading] = useState(false);
    const [sideGameData, setSideGameData] = useState({
        gameName: "",
        subVarient: "",
        buyIn: 0,
        gameStart: "",
    });
    const [sideGameMenuId, setSideGameMenuId] = useState(null);
    const [deleteitem, setDelete] = useState(null)
    // Add these states for options
    const [variantOptions, setVariantOptions] = useState([]);
    const [subVariantOptions, setSubVariantOptions] = useState([]);
    const [gameTypes, setGameTypes] = useState([]);
    const [locationOptions, setLocationOptions] = useState([]);
    const [sessionHands, setSessionHands] = useState([]);
    const [sessionData, setSessionData] = useState({
        gameName: "",
        gameType: "",
        gameLocation: "",
        buyIn: 0,
        gameDate: "",
        gameStart: "",
        pl: 0,
        tsPosition: null,
        tsCount: null,
    });


    const [sideGameResultsModalOpen, setSideGameResultsModalOpen] = useState(false);
    const [sideGameResultsData, setSideGameResultsData] = useState({
        id: null,
        gameEnd: "",
        sessionCashout: "",
    });

    const [sessionResultModalOpen, setSessionResultModalOpen] = useState(false);
    const [sessionResultData, setSessionResultData] = useState({
        id: id,
        gameEnd: "",
        tableSize: "",
        sessionTags: [],
        sessionNotes: "",
        sessionRating: 3,
        sessionDifficulty: 3,
        sessionHeadspace: 3,
        sessionBag: null,
        sessionCashout: "",
        tsPosition: "",
        tsEntrants: "",
        incidentals: "",
    });
    const [tagInput, setTagInput] = useState("");

    // Fetch game types and variants
    useEffect(() => {
        const fetchGameTypes = async () => {
            try {
                const response = await axiosInstance.get(`${API_ENDPOINTS.SELECTSCHEMA}?sessionId=${id}`);
                setGameTypes(response.data.gameTypes || []);
                setVariantOptions(response.data.gameVarients || []);
                setSubVariantOptions(response.data.subGameTypes || []);
            } catch (error) {
                toast.error(error.response?.data?.message || "Failed to fetch game types");
            }
        };
        fetchGameTypes();
    }, [id]);

    useEffect(() => {
        const fetchSessionHands = async () => {
            try {
                const response = await axiosInstance.get(`${API_ENDPOINTS.SESSION_HAND}?sessionId=${id}`);
                setSessionHands(response.data.data || []);
            } catch (error) {
                toast.error(error.response?.data?.message || "Failed to fetch game types");
            }
        };
        fetchSessionHands();
    }, [id]);

    const fetchSessionHands = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`${API_ENDPOINTS.SESSION_HAND}?sessionId=${id}`);
            setSessionHands(response.data.data || []);
        } catch (error) {
            toast.error(error.response?.data?.message);
        } finally {
            setLoading(false);
        }
    }, [id]);

    // Fetch locations
    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const response = await axiosInstance.get(API_ENDPOINTS.LOCATIONS);
                setLocationOptions(
                    (response.data.data || []).map(loc => ({
                        label: loc.name,
                        value: loc.name,
                    }))
                );
            } catch (error) {
                toast.error(error.response?.data?.message || "Failed to fetch locations");
            }
        };
        fetchLocations();
    }, []);

    // Fetch session data
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`${API_ENDPOINTS.SESSIONS}`);
            const sessionData = response.data.data.find(session => session.id === id);
            if (sessionData) {
                setData(sessionData);
            } else {
                toast.error("Session not found");
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    }, [id]);

    // Fetch side game data
    const fetchSideData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`${API_ENDPOINTS.SUBGAME}?parentId=${id}`);
            setSubData(response.data.data || []);
        } catch (error) {
            toast.error(error.response?.data?.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchData();
        fetchSideData();
        fetchSessionHands();
    }, [fetchData, fetchSideData, fetchSessionHands]);

    const fetchShareContent = async (handId) => {
        setShareLoading(true);
        try {
            const response = await axiosInstance.get(`${API_ENDPOINTS.SHARE}?id=${handId}`);
            setShareContent(response.data || "No content available");
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to fetch share content");
            setShareContent("Error fetching shareable content.");
        } finally {
            setShareLoading(false);
        }
    };


    // When data is loaded, sync it to sessionData for editing
    useEffect(() => {
        if (data && Object.keys(data).length > 0) {
            setSessionData({
                gameVarient: data.gameVarient || "",
                gameName: data.gameName || "",
                gameType: data.gameType || "",
                gameStart: data.gameStart || "",
                gameLocation: data.gameLocation || "",
                buyIn: data.buyIn || 0,
            });
        }
    }, [data]);

    // When opening the modal, ensure sessionData is set
    const openEditModal = () => {
        setSessionData({
            gameVarient: data.gameVarient || "",
            gameName: data.gameName || "",
            gameType: data.gameType || "",
            gameStart: data.gameStart || "",
            gameLocation: data.gameLocation || "",
            buyIn: data.buyIn || 0,
        });
        setModalOpen(true);
    };

    const handleSave = async () => {
        setLoading(true);
        const updateData = {
            id: Number(id), // Ensure id is a number
            gameVarient: sessionData.gameVarient,
            gameName: sessionData.gameName,
            gameType: sessionData.gameType,
            gameStart: sessionData.gameStart,
            gameLocation: sessionData.gameLocation,
            buyIn: Number(sessionData.buyIn),
        };
        try {
            const response = await axiosInstance.put(API_ENDPOINTS.SESSIONS, updateData);
            if (response.status === 200) {
                toast.success("Session updated successfully");
                setModalOpen(false);
                fetchData();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSessionData((prev) => ({
            ...prev,
            [name]: value,
        }));
    }

    const handleSideGameChange = (e) => {
        const { name, value } = e.target;
        setSideGameData((prev) => ({
            ...prev,
            [name]: value,
        }));
    }

    const handleSideGameSave = async () => {
        setLoading(true);
        const sideGamePayload = {
            ...sideGameData,
            parentId: Number(id),
            buyIn: Number(sideGameData.buyIn),
        };
        try {
            if (sideGameEditMode === "edit") {
                // Update side game
                const response = await axiosInstance.put(`${API_ENDPOINTS.SUBGAME}`, sideGamePayload);
                if (response.status === 200) {
                    toast.success("Side game updated successfully");
                }
            } else {
                // Create new side game
                const response = await axiosInstance.post(`${API_ENDPOINTS.SUBGAME}`, sideGamePayload);
                if (response.status === 201) {
                    toast.success("Side game added successfully");
                }
            }
            setSideGameModalOpen(false);
            fetchSideData();
        } catch (error) {
            toast.error(error.response?.data?.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    const handleSideResultUpadte = async () => {
        setLoading(true);
        try {
            await axiosInstance.patch(`${API_ENDPOINTS.SUBGAME}`,
                {
                    id: sideGameResultsData.id,
                    gameEnd: sideGameResultsData.gameEnd,
                    sessionCashout: Number(sideGameResultsData.sessionCashout),
                    sessionBag: null,
                }
            );
            toast.success("Results updated!");
            setSideGameResultsModalOpen(false);
            fetchSideData();
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to update results");
        } finally {
            setLoading(false);
        }
    }

    const handleDelete = async () => {
        setLoading(true);
        try {
            if (deleteitem.holding) {
                await axiosInstance.delete(`${API_ENDPOINTS.SESSION_HAND}?id=${deleteitem.id}`);
                fetchSessionHands();
            } else if (deleteitem.id === id) {
                await axiosInstance.delete(`${API_ENDPOINTS.SESSIONS}?id=${deleteitem.id}`);
                navigate(-1)
            }
            else {
                await axiosInstance.delete(`${API_ENDPOINTS.SUBGAME}?id=${deleteitem.id}`);
                fetchSideData();
            }
            toast.success("Deleted successfully");
            setShowModal(false);
        } catch (error) {
            toast.error(error.response?.data?.error || "Something went wrong");
        } finally {
            setLoading(false);
        }
    }


    const handleCancel = () => {
        setShowModal(false);
        setDelete(null);
    }

    const handleSessionResultChange = (e) => {
        const { name, value } = e.target;
        setSessionResultData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleStarChange = (name, value) => {
        setSessionResultData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleAddTag = () => {
        if (tagInput.trim() && !sessionResultData.sessionTags.includes(tagInput.trim())) {
            setSessionResultData((prev) => ({
                ...prev,
                sessionTags: [...prev.sessionTags, tagInput.trim()],
            }));
            setTagInput("");
        }
    };

    const handleRemoveTag = (tag) => {
        setSessionResultData((prev) => ({
            ...prev,
            sessionTags: prev.sessionTags.filter(t => t !== tag),
        }));
    };

    const handleSessionResultSave = async () => {
        setLoading(true);
        try {
            await axiosInstance.patch(
                API_ENDPOINTS.SESSIONS,
                {
                    ...sessionResultData,
                    gameEnd: sessionResultData.gameEnd
                        ? dayjs(sessionResultData.gameEnd).format('YYYY-MM-DD HH:mm:ss')
                        : "",
                    sessionTags: JSON.stringify(sessionResultData.sessionTags),
                    sessionCashout: Number(sessionResultData.sessionCashout),
                    incidentals: Number(sessionResultData.incidentals),
                    tsPosition: Number(sessionResultData.tsPosition),
                    tsEntrants: Number(sessionResultData.tsEntrants),
                }
            );
            toast.success("Session result updated!");
            setSessionResultModalOpen(false);
            fetchData();
            fetchSideData();
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to update session result");
        } finally {
            setLoading(false);
        }
    };

    const updateHand = (hand) => {
        navigate(`/Sessions/SessionCard/addhand/${id}`, {
            state: { prevHand: hand }
        })
    }

    const getBoardCards = (board) => {
        if (!board) return [];
        return board.match(/.{1,2}/g) || [];
    };

    return (
        <div className="bg-black min-h-screen text-white font-sans p-6">
            {loading &&
                <ThreeDots
                    visible={true}
                    height="80"
                    width="80"
                    color="#1976d2"
                    radius="9"
                    ariaLabel="three-dots-loading"
                    wrapperStyle={{}}
                    wrapperClass="dots-loading additional-class"
                />
            }
            {/* Header */}
            <div className="flex flex-wrap items-center mb-6">
                <button className="border border-border hover:bg-[#181818] text-white text-xl mr-3 w-10 h-10 flex items-center justify-center rounded-lg "
                    onClick={() => navigate(-1)}
                >
                    <IoChevronBackOutline />
                </button>
                <h1 className="font-bold text-xl m-0 longText ">{data.gameName}</h1>
                <div className="flex-1" />
                <div className="flex gap-3 mt-5 md:mt-0 flex-wrap text-nowrap">
                    <button className="border border-border flex-1 md:flex-1 hover:bg-[#181818] text-white text-sm py-2 px-4 gap-3 flex items-center justify-center rounded-lg "
                        onClick={() => {
                            setSessionResultData({
                                id: Number(id),
                                gameEnd: data.gameEnd ? new Date(data.gameEnd).toISOString().slice(0, 16) : "",
                                tableSize: data.tableSize || "",
                                sessionTags: data.sessionTags ? JSON.parse(data.sessionTags) : [],
                                sessionNotes: data.sessionNotes || "",
                                sessionRating: data.sessionRating || 3,
                                sessionDifficulty: data.sessionDifficulty || 3,
                                sessionHeadspace: data.sessionHeadspace || 3,
                                sessionBag: data.sessionBag || null,
                                sessionCashout: data.sessionCashout || "",
                                tsPosition: data.tsPosition || "",
                                tsEntrants: data.tsEntrants || "",
                                incidentals: data.incidentals || "",
                            });
                            setSessionResultModalOpen(true);
                        }}
                    >
                        <HiPencilAlt />
                        Result
                    </button>
                    <button className="border border-border flex-1 md:flex-1 hover:bg-[#181818] text-white text-sm py-2 px-4 gap-3 flex items-center justify-center rounded-lg "
                        onClick={openEditModal}
                    >
                        <BsPencil />
                        Edit Session
                    </button>
                    <button className="border border-border flex-1 md:flex-1 hover:bg-[#181818] text-white text-sm py-2 px-4 gap-3 flex items-center justify-center rounded-lg "
                        onClick={() => {
                            setShowModal(true);
                            setDelete(data)
                        }}
                    >
                        <MdDelete />
                        Delete Session
                    </button>
                    <Link to={`/Sessions/SessionCard/addhand/${id}`} className="bg-secondary flex-2 md:flex-1 w-full justify-center  hover:opacity-75 px-4 py-2 rounded text-sm text-white flex items-center gap-2  ">
                        <span className="text-xl font-bold">+</span>
                        Add Hand
                    </Link>
                </div>
            </div>

            {/* Tournament Info */}
            <div className="border border-border rounded-xl p-6 mb-6 flex flex-col md:flex-row justify-between items-center">
                <div className="w-full md:w-auto">
                    <div className="flex items-center mb-4">
                        <span className="mr-2 text-xl">
                            <FaTrophy className="text-blue-500" />
                        </span>
                        <span className="font-semibold mr-2">{data.gameType}</span>
                        <span className="text-[#a78bfa] font-bold mr-2">•</span>
                        <span className="text-gray-400">{data.gameVarient}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-8 gap-y-2 text-gray-300 text-sm">
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                                <span className="text-md">
                                    <FaMapMarkerAlt />
                                </span>
                                <span>{data.gameLocation || "Location"}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-lg"><FaDollarSign /></span>
                                <span>Buy In: ${parseFloat(data.buyIn || 0).toLocaleString()}</span>
                            </div>
                        </div>
                        <div className="flex flex-col gap-2 w-[230px]">
                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2 flex-1">
                                    <span className="text-md">
                                        <FaCalendarAlt />
                                    </span>
                                    <span>
                                        {data.gameDate
                                            ? new Date(data.gameDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
                                            : "Date"}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 flex-1">
                                    <span className="text-md">
                                        <FaClock />
                                    </span>
                                    <span>
                                        {data.gameStart
                                            ? new Date(data.gameStart).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                            : "--:--"}
                                        {/* Duration can be calculated if gameEnd is present */}
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2 flex-1">
                                    <span className="text-md">
                                        <SiCashapp />
                                    </span>
                                    <span>
                                        ${data.sessionCashout}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 flex-1">
                                    <span className="text-md">
                                        <IoReceipt />
                                    </span>
                                    <span>
                                        ${data.incidentals}
                                    </span>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
                <div className="text-right mt-4 md:mt-0 w-full md:w-auto">
                    <div className={`font-bold text-2xl  ${parseFloat(data.pl) >= 0 ? "text-[#3fcf7f]" : "text-red-400"}`}>
                        {parseFloat(data.pl) >= 0 ? "+" : ""}${parseFloat(data.pl || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                    {/* Example: 3rd of 48, you can update this if you have position/entrants */}
                    <div className="text-gray-400 text-base">{data.tsPosition && data.tsEntrants ? `${data.tsPosition} of ${data.tsEntrants}` : null}</div>
                </div>
            </div>

            {/* Side Games */}
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-xl mb-0">Side Games and Options</h2>
                <button className="bg-secondary text-sm hover:opacity-75 px-4 py-1 rounded text-white flex items-center gap-2  "
                    onClick={() => {
                        setSideGameData({
                            gameName: "",
                            subVarient: "",
                            buyIn: 0,
                            gameStart: "",
                        });
                        setSideGameEditMode("add");
                        setSideGameModalOpen(true);
                    }}
                >
                    <span className="text-xl font-bold">+</span>
                    Add Side Game
                </button>
            </div>
            <div className={`border border-border overflow-auto xl:overflow-visible scrollbar-hide text-nowrap  rounded-xl mb-8 `}>
                <table className="w-full border-collapse">
                    <thead>
                        <tr className="text-gray-400 text-left">
                            <th className="px-4 py-3 font-normal">Name</th>
                            <th className="px-4 py-3 font-normal">Type</th>
                            <th className="px-4 py-3 font-normal">Buy In</th>
                            <th className="px-4 py-3 font-normal">Cash Out</th>
                            <th className="px-4 py-3 font-normal">P/L</th>
                            <th className="px-4 py-3 font-normal"></th>
                        </tr>
                    </thead>
                    <tbody className="relative">
                        {subData.length === 0 && (
                            <tr>
                                <td colSpan="5" className="px-4 py-3 text-center text-gray-400">
                                    No side games available
                                </td>
                            </tr>
                        )}
                        {subData.map((game , index) => (
                            <tr key={game.id} className="border-t border-[#222] hover:bg-zinc-900 transition text-sm">
                                <td className="px-4 py-3 font-semibold">{game.gameName}</td>
                                <td className="px-4 py-3">{game.subVarient}</td>
                                <td className="px-4 py-3">${parseFloat(game.buyIn || 0).toLocaleString()}</td>
                                <td className="px-4 py-3">${parseFloat(game.sessionCashout || 0).toLocaleString()}</td>
                                <td className={`px-4 py-3 ${game.pl >= 0 ? 'text-green-500' : 'text-red-500'}`}>{game.pl}</td>
                                <td className="px-4 py-3  text-gray-400 relative">
                                    <button
                                        className="text-gray-400 hover:text-white"
                                        onClick={() => setSideGameMenuId(game.id)}
                                    >
                                        <FaEllipsisV />
                                    </button>
                                    {sideGameMenuId === game.id && (

                                        <div
                                            className={`absolute -left-[113px] mt-2 w-32 bg-zinc-800 border border-zinc-700 rounded shadow-lg z-10
                                            ${index === 0 ? '-top-[37px]' : ' bottom-0'}`}
                                        >
                                            <button
                                                onClick={() => {
                                                    setSideGameData({
                                                        ...game,
                                                        gameStart: game.gameStart ? new Date(game.gameStart).toISOString().slice(0, 16) : "",
                                                    });
                                                    setSideGameEditMode("edit");
                                                    setSideGameModalOpen(true);
                                                    setSideGameMenuId(null);
                                                }}
                                                className="flex items-center gap-2 px-4 py-2 hover:bg-zinc-700 w-full text-left text-sm"
                                            >
                                                <FaEdit className="text-white" />
                                                Edit
                                            </button>
                                            <button
                                                className="flex items-center gap-2 px-4 py-2 hover:bg-zinc-700 w-full text-left text-sm"
                                                onClick={() => {
                                                    setSideGameResultsData({
                                                        id: game.id,
                                                        gameEnd: game.gameEnd
                                                            ? new Date(game.gameEnd).toISOString().slice(0, 16)
                                                            : "",
                                                        sessionCashout: game.sessionCashout || "",
                                                    });
                                                    setSideGameResultsModalOpen(true);
                                                    setSideGameMenuId(null);
                                                }}
                                            >
                                                <HiPencilAlt />
                                                Results
                                            </button>
                                            <button
                                                className="flex items-center gap-2 px-4 py-2 hover:bg-zinc-700 w-full text-left text-sm text-red-400"
                                                onClick={() => {
                                                    setShowModal(true);
                                                    setDelete(game)
                                                }}
                                            >
                                                <FaTrash />
                                                Delete
                                            </button>
                                        </div>
                                    )}
                                    {/* Close menu when clicking outside */}
                                    {sideGameMenuId === game.id && (
                                        <div
                                            className="fixed inset-0 z-9"
                                            onClick={() => setSideGameMenuId(null)}
                                            tabIndex={-1}
                                        />
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Hands */}
            {sessionHands.length > 0 &&
                <h2 h2 className="font-bold text-xl mb-3">Hands</h2>
            }
            {
                sessionHands.length > 0 &&
                sessionHands.map((hand) => (
                    <div className="border border-border flex justify-between  rounded-xl p-6 mb-4 relative">
                        <div className=" grid grid-cols-1 md:grid-cols-2 gap-5 flex-[2] cursor-pointer " onClick={() => updateHand(hand)}>
                            <div className="text-gray-400 text-sm ">
                                <div className="font-bold text-lg mb-2 flex gap-2 items-center">
                                    <div className="flex items-center gap-1 black-spade ">
                                        {getBoardCards(hand.holding).map((card, idx) => (
                                            <div
                                                key={idx}
                                                className="w-12 h-16 cursor-not-allowed bg-white rounded-lg text-black flex items-center flex-col justify-center text-lg font-bold"
                                            >
                                                <span className="pt-1">{card[0]}</span>
                                                <span className={`card-img ${card[1]}`}></span>
                                            </div>
                                        ))}
                                    </div>

                                </div>
                                <div className="flex gap-10">
                                    <div>
                                        <div>
                                            Postion:<span className="text-white"> {hand.position}</span>
                                        </div>
                                        <div >
                                            Handed: <span className="text-white">{hand.handed}</span>
                                        </div>
                                    </div>
                                    <div>
                                        <div>
                                            Blinds: <span className="text-white">{hand.blinds}</span>
                                        </div>
                                        <div>
                                            Pot: <span className="text-[#3fcf7f]">{hand.pot}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col gap-3 items-start">

                                <div className="text-gray-400 text-sm">
                                    Board:
                                    <div className="flex items-center gap-1 black-spade ">
                                        {getBoardCards(hand.board).map((card, idx) => (
                                            <div
                                                key={idx}
                                                className="w-12 h-16 cursor-not-allowed  bg-white rounded-lg text-black flex items-center flex-col justify-center text-lg font-bold"
                                            >
                                                <span className="pt-1">{card[0]}</span>
                                                <span className={`card-img ${card[1]}`}></span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end  text-gray-400 cursor-pointer text-xl flex-[1] absolute md:static right-2 top-2 z-10">
                            <button className=" hover:bg-[#181818] text-white text-xl w-10 h-10 flex items-center justify-center rounded-lg"
                                onClick={() => {
                                    setShareModalOpen(true);
                                    fetchShareContent(hand.id);
                                }}
                            >
                                <CiShare2 />
                            </button>
                            <button className=" hover:bg-[#181818] hover:text-red-600 text-white text-xl w-10 h-10 flex items-center justify-center rounded-lg"
                                onClick={() => {
                                    setShowModal(true);
                                    setDelete(hand)
                                }}
                            >
                                <MdDelete />
                            </button>
                        </div>
                    </div>
                ))

            }


            <SessionModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onSave={handleSave}
                mode="edit"
                data={sessionData}
                onChange={handleChange}
                variantOptions={variantOptions}
                typeOptions={gameTypes}
                locationOptions={locationOptions}
            />
            {/* add and edit sidegame modal  */}
            {
                sideGameModalOpen && (
                    <div className="fixed inset-0 px-3  flex items-center justify-center bg-black bg-opacity-60 z-[99999]">
                        <div className="bg-[#111] border border-border rounded-xl p-4 md:p-8 w-full max-w-lg shadow-lg relative">
                            <button
                                className="absolute top-4 right-4 text-gray-400 hover:text-white text-xl"
                                onClick={() => setSideGameModalOpen(false)}
                            >
                                &times;
                            </button>
                            <h2 className="text-white text-2xl font-bold mb-6">
                                {sideGameEditMode === "edit" ? `Edit ${sideGameData.gameName}` : "Add Side Game"}
                            </h2>
                            <form
                                onSubmit={e => {
                                    e.preventDefault();
                                    handleSideGameSave();
                                }}
                            >
                                <div className="mb-4">
                                    <label className="block text-gray-300 mb-1">Name</label>
                                    <input
                                        className="w-full bg-black border border-border rounded px-3 py-2 text-white focus:outline-none focus:border-blue-500"
                                        name="gameName"
                                        placeholder="e.g., Last Player Standing"
                                        value={sideGameData.gameName || ""}
                                        onChange={handleSideGameChange}
                                        required
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-gray-300 mb-1">Type</label>
                                    <Select
                                        name="subVarient"
                                        options={subVariantOptions}
                                        value={subVariantOptions.find(option => option.value === sideGameData.subVarient) || null}
                                        onChange={(option) => handleSideGameChange({ target: { name: "subVarient", value: option.value } })}
                                        placeholder="Select type"
                                        isClearable
                                        styles={customStyles}
                                    />

                                </div>
                                <div className="mb-4">
                                    <label className="block text-gray-300 mb-1">Buy In</label>
                                    <input
                                        className="w-full bg-black border border-border rounded px-3 py-2 text-white focus:outline-none focus:border-blue-500"
                                        name="buyIn"
                                        placeholder="e.g., 20"
                                        type="number"
                                        value={sideGameData.buyIn || ""}
                                        onChange={handleSideGameChange}
                                        required
                                    />
                                </div>
                                <div className="mb-6">
                                    <label className="block text-gray-300 mb-1">Game Start</label>
                                    <div className="relative">
                                        <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                                        <DatePicker
                                            selected={sideGameData.gameStart ? new Date(sideGameData.gameStart) : null}
                                            onChange={(date) =>
                                                setSideGameData((prev) => ({
                                                    ...prev,
                                                    gameStart: date ? date.toISOString() : "",
                                                }))
                                            }
                                            showTimeSelect
                                            dateFormat="do MMMM yyyy HH:mm"
                                            placeholderText="Select date and time"
                                            className="w-full pl-10 pr-3 py-3 border border-border rounded bg-black text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            calendarClassName="custom-datepicker"
                                        />
                                    </div>

                                </div>
                                <div className="flex justify-end gap-2">
                                    <button
                                        type="button"
                                        className="bg-[#222] text-white px-4 py-2 rounded"
                                        onClick={() => setSideGameModalOpen(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="bg-secondary  hover:opacity-75 px-4 py-2 rounded text-white flex items-center gap-2 "
                                    >
                                        {sideGameEditMode === "edit" ? "Save Changes" : "Add Side Game"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }

            {/* side game update result modal */}

            {
                sideGameResultsModalOpen && (
                    <div className="fixed inset-0 z-[99999] flex items-center justify-center px-3 bg-black bg-opacity-60">
                        <div className="bg-[#111] border border-border rounded-xl p-4 md:p-8 w-full max-w-lg shadow-lg relative">
                            <button
                                className="absolute top-4 right-4 text-gray-400 hover:text-white text-xl"
                                onClick={() => setSideGameResultsModalOpen(false)}
                            >
                                &times;
                            </button>
                            <h2 className="text-white text-2xl font-bold mb-6">
                                Side Game Results
                            </h2>
                            <div className="mb-4">
                                <label className="block text-gray-300 mb-1">Game End</label>
                                <div className=" relative ">
                                    <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                                    <DatePicker
                                        selected={sideGameResultsData.gameEnd ? new Date(sideGameResultsData.gameEnd) : null}
                                        onChange={(date) =>
                                            setSideGameResultsData((prev) => ({
                                                ...prev,
                                                gameEnd: date ? dayjs(date).format('YYYY-MM-DD HH:mm:ss') : "",
                                            }))
                                        }
                                        showTimeSelect
                                        dateFormat="do MMMM yyyy HH:mm"
                                        placeholderText="Select date and time"
                                        className="w-full pl-10 pr-3 py-3 border border-border rounded bg-black text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        calendarClassName="custom-datepicker"
                                    />
                                </div>
                            </div>
                            <div className="mb-6">
                                <label className="block text-gray-300 mb-1">Cash Out</label>
                                <input
                                    className="w-full bg-black border border-border rounded px-3 py-2 text-white focus:outline-none focus:border-blue-500"
                                    name="sessionCashout"
                                    placeholder="e.g., 100"
                                    type="number"
                                    value={sideGameResultsData.sessionCashout}
                                    onChange={e =>
                                        setSideGameResultsData(d => ({
                                            ...d,
                                            sessionCashout: e.target.value,
                                        }))
                                    }
                                    required
                                />
                            </div>
                            <div className="flex justify-end gap-2">
                                <button
                                    type="button"
                                    className="bg-[#222] text-white px-4 py-2 rounded"
                                    onClick={() => setSideGameResultsModalOpen(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSideResultUpadte}
                                    className="bg-secondary  hover:opacity-75 px-4 py-2 rounded text-white flex items-center gap-2 "
                                >
                                    Save Results
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* modal for full result */}

            {
                sessionResultModalOpen && (
                    <div className="fixed inset-0 z-[99999] flex items-center px-3 justify-center bg-black bg-opacity-60">
                        <div className="bg-[#111] border border-border rounded-xl p-4 md:p-8 w-full max-w-xl h-[80vh] overflow-auto scrollbar-hide shadow-lg relative">
                            <button
                                className="absolute top-4 right-4 text-gray-400 hover:text-white text-xl"
                                onClick={() => setSessionResultModalOpen(false)}
                            >
                                &times;
                            </button>
                            <h2 className="text-white text-2xl font-bold mb-6">
                                Update Session Result
                            </h2>
                            <form
                                onSubmit={e => {
                                    e.preventDefault();
                                    handleSessionResultSave();
                                }}
                            >
                                <div className="mb-4">
                                    <label className="block text-gray-300 mb-1">Game End</label>
                                    <div className="relative">
                                        <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                                        <DatePicker
                                            selected={sessionResultData.gameEnd ? new Date(sessionResultData.gameEnd) : null}
                                            onChange={(date) =>
                                                setSessionResultData((prev) => ({
                                                    ...prev,
                                                    gameEnd: date ? date.toISOString() : "",
                                                }))
                                            }
                                            showTimeSelect
                                            dateFormat="do MMMM yyyy HH:mm"
                                            placeholderText="Select date and time"
                                            className="w-full pl-10 pr-3 py-3 border border-border rounded bg-black text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            calendarClassName="custom-datepicker"
                                        />
                                    </div>
                                </div>
                                <div className="flex gap-4 mb-4">
                                    <div className="flex-1">
                                        <label className="block text-gray-300 mb-1">Table Size</label>
                                        <select
                                            className="w-full bg-black border border-border rounded px-3 py-2 text-white"
                                            name="tableSize"
                                            value={sessionResultData.tableSize}
                                            onChange={handleSessionResultChange}
                                        >
                                            <option value="">Select table size</option>
                                            <option value="6">6</option>
                                            <option value="8">8</option>
                                            <option value="9">9</option>
                                            <option value="10">10</option>
                                        </select>
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-gray-300 mb-1">Cash Out</label>
                                        <input
                                            className="w-full bg-black border border-border rounded px-3 py-2 text-white"
                                            name="sessionCashout"
                                            placeholder="e.g., 1500"
                                            type="number"
                                            value={sessionResultData.sessionCashout}
                                            onChange={handleSessionResultChange}
                                        />
                                    </div>
                                </div>
                                <div className="flex gap-4 mb-4 flex-wrap">
                                    {["sessionRating", "sessionHeadspace", "sessionDifficulty"].map((field, idx) => (
                                        <div className="flex-1" key={field}>
                                            <label className="block text-gray-300 mb-1">
                                                {field === "sessionRating" && "Session Rating"}
                                                {field === "sessionHeadspace" && "Session Headspace"}
                                                {field === "sessionDifficulty" && "Session Difficulty"}
                                            </label>
                                            <div className="flex items-center gap-1">
                                                {[1, 2, 3, 4, 5].map(star => (
                                                    <span
                                                        key={star}
                                                        className={`cursor-pointer text-xl ${sessionResultData[field] >= star ? "text-yellow-400" : "text-gray-500"}`}
                                                        onClick={() => handleStarChange(field, star)}
                                                    >★</span>
                                                ))}
                                                <span className="ml-2 text-gray-400 text-sm">({sessionResultData[field]}/5)</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex gap-4 mb-4">
                                    <div className="flex-1">
                                        <label className="block text-gray-300 mb-1">Incidentals</label>
                                        <input
                                            className="w-full bg-black border border-border rounded px-3 py-2 text-white"
                                            name="incidentals"
                                            type="number"
                                            value={sessionResultData.incidentals}
                                            onChange={handleSessionResultChange}
                                        />
                                    </div>
                                </div>
                                <div className="flex gap-4 mb-4">
                                    <div className="flex-1">
                                        <label className="block text-gray-300 mb-1">Entrants</label>
                                        <input
                                            className="w-full bg-black border border-border rounded px-3 py-2 text-white"
                                            name="tsEntrants"
                                            type="number"
                                            value={sessionResultData.tsEntrants}
                                            onChange={handleSessionResultChange}
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-gray-300 mb-1">Position</label>
                                        <input
                                            className="w-full bg-black border border-border rounded px-3 py-2 text-white"
                                            name="tsPosition"
                                            type="number"
                                            value={sessionResultData.tsPosition}
                                            onChange={handleSessionResultChange}
                                        />
                                    </div>
                                </div>
                                <div className="mb-4">
                                    <label className="block text-gray-300 mb-1">Session Notes</label>
                                    <textarea
                                        className="w-full bg-black border border-border rounded px-3 py-2 text-white"
                                        name="sessionNotes"
                                        placeholder="Enter session notes..."
                                        value={sessionResultData.sessionNotes}
                                        onChange={handleSessionResultChange}
                                        rows={3}
                                    />
                                </div>
                                <div className="mb-6">
                                    <label className="block text-gray-300 mb-1">Session Tags</label>
                                    <div className="flex gap-2 mb-2 flex-wrap">
                                        {sessionResultData.sessionTags.map(tag => (
                                            <span
                                                key={tag}
                                                className="bg-zinc-800 text-white px-2 py-1 rounded flex items-center gap-1"
                                            >
                                                {tag}
                                                <button
                                                    type="button"
                                                    className="ml-1 text-red-400"
                                                    onClick={() => handleRemoveTag(tag)}
                                                >×</button>
                                            </span>
                                        ))}
                                    </div>
                                    <div className="flex gap-2">
                                        <input
                                            className="flex-1 bg-black border border-border rounded px-3 py-2 text-white"
                                            placeholder="Add a tag..."
                                            value={tagInput}
                                            onChange={e => setTagInput(e.target.value)}
                                            onKeyDown={e => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    handleAddTag();
                                                }
                                            }}
                                        />
                                        <button
                                            type="button"
                                            className="bg-[#222] text-white px-4 py-2 rounded"
                                            onClick={handleAddTag}
                                        >
                                            Add
                                        </button>
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2">
                                    <button
                                        type="button"
                                        className="bg-[#222] text-white px-4 py-2 rounded"
                                        onClick={() => setSessionResultModalOpen(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="bg-secondary  hover:opacity-75 px-4 py-2 rounded text-white flex items-center gap-2 "
                                    >
                                        Save Results
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }

            {/* share modal */}
            {shareModalOpen && (
                <div className="fixed inset-0 z-[99999] flex items-center justify-center px-3 bg-black bg-opacity-60">
                    <div className="bg-[#111] border border-border rounded-xl p-6 w-full max-w-md shadow-lg relative">
                        <button
                            className="absolute top-4 right-4 text-gray-400 hover:text-white text-xl"
                            onClick={() => {
                                setShareModalOpen(false);
                                setShareContent("");
                            }}
                        >
                            &times;
                        </button>
                        <h2 className="text-white text-xl font-bold mb-4">Share Hand</h2>
                        {shareLoading ? (
                            <p className="text-gray-400">Loading share content...</p>
                        ) : (
                            <>
                                <div className="bg-black border border-zinc-700 p-3 rounded mb-4 text-sm text-gray-300 whitespace-pre-wrap">
                                    {shareContent}
                                </div>
                                <div className="flex justify-end gap-2">
                                    <button
                                        className="bg-[#222] text-white px-4 py-2 rounded"
                                        onClick={() => setShareModalOpen(false)}
                                    >
                                        Close
                                    </button>
                                    <button
                                        className="bg-secondary  hover:opacity-75 px-4 py-2 rounded text-white flex items-center gap-2"
                                        onClick={() => {
                                            navigator.clipboard.writeText(shareContent);
                                            setShareModalOpen(false);
                                            toast.success("Copied to clipboard!");
                                        }}
                                    >
                                        Copy to Clipboard
                                    </button>
                                    <button
                                        className="bg-[#1976d230]  hover:opacity-75 px-4 py-2 rounded text-white flex items-center gap-2"
                                        onClick={() => {
                                            setShareModalOpen(false);
                                            // navigate(`/Sessions/Replay/${selectedHandId}`);
                                        }}
                                    >
                                        Replay
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}


            {/* delete side game  */}

            <ConfirmationModal
                open={showModal}
                title="Are you sure?"
                message={`This will permanently delete the "${deleteitem?.gameName || `${deleteitem?.holding} Hand`}". This action cannot be undone.`}
                onCancel={handleCancel}
                onConfirm={handleDelete}
            />

        </div >
    );
};

export default SessionCard;
